import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';

/**
 * Narrow a locale code to a language this app has a catalogue for.
 *
 * Accepts region-tagged codes — `fr-ca`, `nl-BE`, `en_GB` — and keeps only the
 * leading subtag. Anything we don't ship returns null so the caller can move on
 * to the next source.
 *
 * Splits on the subtag separator rather than slicing two characters, so codes
 * that merely *start* with our letters — `frr` (Northern Frisian), `enm` — do
 * not get mistaken for languages we ship.
 */
export const toSupported = (value: string | null | undefined): Language | null => {
  const code = value?.trim().split(/[-_]/)[0].toLowerCase();
  return SUPPORTED_LANGUAGES.find(language => language === code) ?? null;
};

interface TenantLanguages {
  languages: readonly Language[];
  fallback: Language;
  /** Whether this tenant shows the in-app switcher at all. */
  switcher: boolean;
}

/**
 * What each tenant ships, and what it falls back to.
 *
 * GAM-729 is scoped to Circus Daily. This app is multi-tenant — `cd`, `dk` and
 * `sky` all resolve through `resolveTenantFromUrl()`, which also reads a
 * `/tenant/:code` path prefix at runtime — so without a gate the switcher would
 * offer DraftKings and Skypicks players languages those tenants have no content
 * for, and the English default would flip under them too. `dk` and `sky`
 * therefore keep exactly what they had before GAM-729: English and French, with
 * French as both default and fallback.
 */
const TENANT_LANGUAGES: Record<string, TenantLanguages> = {
  cd: { languages: ['en', 'fr', 'nl'], fallback: DEFAULT_LANGUAGE, switcher: true },
  dk: { languages: ['en', 'fr'], fallback: 'fr', switcher: false },
  sky: { languages: ['en', 'fr'], fallback: 'fr', switcher: false },
};

/** An unrecognised tenant is treated as the pre-GAM-729 app was. */
const UNKNOWN_TENANT: TenantLanguages = {
  languages: ['en', 'fr'],
  fallback: 'fr',
  switcher: false,
};

const tenantConfig = (): TenantLanguages =>
  TENANT_LANGUAGES[resolveTenantFromUrl()] ?? UNKNOWN_TENANT;

/** The languages this tenant offers, in switcher order. */
export const tenantLanguages = (): readonly Language[] => tenantConfig().languages;

/** This tenant's default and i18next `fallbackLng`. */
export const tenantFallbackLanguage = (): Language => tenantConfig().fallback;

/** Whether this tenant shows the in-app switcher. Circus Daily only, per GAM-729. */
export const isSwitcherEnabled = (): boolean => tenantConfig().switcher;

/** Narrow a locale code to a language *this tenant* actually offers. */
export const toTenantSupported = (value: string | null | undefined): Language | null => {
  const language = toSupported(value);
  return language && tenantLanguages().includes(language) ? language : null;
};

/**
 * BCP-47 tags for `Intl` — dates, times and number formatting.
 *
 * Dutch is Belgian (24-hour clock, Belgian month names). English and French
 * keep the tags the app already used: this frontend also serves the DraftKings
 * and Skypicks tenants, so their formatting must not shift under them.
 */
export const DATE_LOCALES: Record<Language, string> = {
  en: 'en-US',
  fr: 'fr-CA',
  nl: 'nl-BE',
};

/** Where a player's language is remembered between visits. */
export const LANGUAGE_STORAGE_KEY = 'pickem.lang';

/**
 * Marks the remembered language as the player's own, made in the switcher,
 * rather than one the host handed us in the URL.
 */
const CHOSEN_FLAG_KEY = 'pickem.lang.chosen';

/** localStorage throws outright in private windows, so every access is guarded. */
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows: the choice still applies for this visit.
  }
};

const remove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Private windows: nothing was stored to begin with.
  }
};

/**
 * Record the language the player picked in the switcher.
 *
 * Flagged as theirs so a `?locale=` on the next entry cannot quietly undo it.
 */
export const storeLanguage = (language: Language): void => {
  write(LANGUAGE_STORAGE_KEY, language);
  write(CHOSEN_FLAG_KEY, '1');
};

/**
 * Remember what the host asked for, without claiming the player chose it.
 *
 * The chosen marker is cleared, not left alone: it belongs to the value being
 * overwritten, so keeping it would forge a choice the player never made — a
 * `?locale=` on one tenant would otherwise overwrite `pickem.lang` while the
 * flag still said "theirs", and the real choice they made on another tenant
 * would be gone for good.
 */
const rememberHostLanguage = (language: Language): void => {
  write(LANGUAGE_STORAGE_KEY, language);
  remove(CHOSEN_FLAG_KEY);
};

/**
 * Resolve the language for this visit.
 *
 * In order: a language the player chose in the switcher, the URL the host page
 * sent us to, a language remembered from an earlier visit, the browser's own
 * language, and this tenant's fallback.
 *
 * The player's own choice deliberately outranks `?locale=`. The host launches
 * the game with a locale on entry, so the other way round a player who picked
 * Dutch would be back in French the moment they re-entered — and the choice
 * would never survive, which is the point of having a switcher. A host locale
 * is still honoured for anyone who has not chosen, and still remembered, so it
 * only has to be sent once.
 *
 * Every source is narrowed to what *this tenant* offers, so a `pickem.lang` of
 * `nl` carried over from Circus Daily on a shared origin cannot put DraftKings
 * into Dutch.
 */
export const detectLanguage = (): Language => {
  const remembered = toTenantSupported(read(LANGUAGE_STORAGE_KEY));

  if (remembered && read(CHOSEN_FLAG_KEY) === '1') {
    return remembered;
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = toTenantSupported(params.get('locale')) ?? toTenantSupported(params.get('lang'));

  if (fromUrl) {
    rememberHostLanguage(fromUrl);
    return fromUrl;
  }

  return remembered ?? toTenantSupported(navigator.language) ?? tenantFallbackLanguage();
};
